import * as React from 'react';
import { GetInfluencerQuery } from 'generated/graphql';
import { ReactComponent as GoogleLogoSvg } from '../../../auth/view/assets/google-logo.svg';
import { ReactComponent as AppleLogoSvg } from '../../../auth/view/assets/apple-logo.svg';
import { useConnectedAccounts } from './useConnectedAccounts.hook';
import { AccountRow } from './AccountRow';
import { ApplePrivateRelayBanner } from './ApplePrivateRelayBanner';
import { DisconnectConfirmationModal } from './DisconnectConfirmationModal';
import * as styles from './ConnectedAccountsSection.css';
import { SocialProvider } from './account.types';
import { UpdateSignInEmailFlow } from '@client/new/features/update-sign-in-email/UpdateSignInEmailFlow';

type Props = {
  socialAccounts: NonNullable<GetInfluencerQuery['creator']>['socialAccounts'];
  onRefresh: () => Promise<unknown> | void;
};

const providerConfigs: ReadonlyArray<{
  provider: SocialProvider;
  label: string;
  Logo: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  logoClassName?: string;
}> = [
  {
    provider: 'google',
    label: 'Google',
    Logo: GoogleLogoSvg,
    logoClassName: undefined,
  },
  {
    provider: 'apple',
    label: 'Apple',
    Logo: AppleLogoSvg,
    logoClassName: styles.appleLogo,
  },
];

export const ConnectedAccountsSection = ({
  socialAccounts,
  onRefresh,
}: Props) => {
  const {
    connectedByProvider,
    pendingProvider,
    handleConnect,
    disconnect,
    confirmDisconnectProvider,
    handleConfirmDisconnect,
    cancelConfirmDisconnect,
    currentEmail,
    hasPrivateRelayEmail,
    openAddEmailFlow,
    closeUpdateSignInEmailFlow,
    isAddEmailFlowOpen,
    isDisconnectRelayFlowOpen,
    handleAddEmailFlowCompleted,
    handleAddEmailFlowAbandoned,
    handleDisconnectRelayFlowCompleted,
    handleDisconnectRelayFlowAbandoned,
  } = useConnectedAccounts({ socialAccounts, onRefresh });

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>Connected accounts</h2>
      <div className={styles.list}>
        {providerConfigs.map(({ provider, label, Logo, logoClassName }) => (
          <AccountRow
            key={provider}
            label={label}
            email={connectedByProvider[provider].email}
            Logo={Logo}
            logoClassName={logoClassName}
            connected={connectedByProvider[provider].connected}
            isLoading={pendingProvider === provider}
            onConnect={() => handleConnect(provider)}
            onDisconnect={() => disconnect(provider)}
          />
        ))}
      </div>
      {hasPrivateRelayEmail && (
        <ApplePrivateRelayBanner onAddEmail={openAddEmailFlow} />
      )}
      <UpdateSignInEmailFlow
        isOpen={isAddEmailFlowOpen}
        onClose={closeUpdateSignInEmailFlow}
        onAbandon={handleAddEmailFlowAbandoned}
        currentEmail={currentEmail}
        onSuccess={handleAddEmailFlowCompleted}
      />
      <UpdateSignInEmailFlow
        isOpen={isDisconnectRelayFlowOpen}
        onClose={closeUpdateSignInEmailFlow}
        onAbandon={handleDisconnectRelayFlowAbandoned}
        currentEmail={currentEmail}
        title="Disconnect account"
        description="To disconnect Apple, you need another way to sign in to your account. Enter an email address to use for secure login with a verification code."
        onSuccess={handleDisconnectRelayFlowCompleted}
      />
      <DisconnectConfirmationModal
        isOpen={confirmDisconnectProvider !== null}
        email={currentEmail ?? ''}
        onConfirm={handleConfirmDisconnect}
        onCancel={cancelConfirmDisconnect}
      />
    </section>
  );
};
